import { AssetType } from "../../types/AssetType";
import { OrderProcessingType } from "../../types/OrderProcessingType";
import { TargetPlatform } from "../../types/TargetPlatform";

export type FeeCalculationParameters = {
    targetPlatform: TargetPlatform;
    assetType: AssetType;
    processingType: OrderProcessingType;
    size: number;
    price: number;
    underlyingPrice: number | null;
};

export interface IPlatformFeeCalculationService {
    getForSpots(parameters: FeeCalculationParameters): number;
    getForFutures(parameters: FeeCalculationParameters): number;
    getForOptions(parameters: FeeCalculationParameters): number;
}

class BinanceFeeCalculationService implements IPlatformFeeCalculationService {
    getForSpots(parameters: FeeCalculationParameters): number {
        return Math.abs(parameters.size) * parameters.price * 0.001;
    }

    getForFutures(parameters: FeeCalculationParameters): number {
        const rate = parameters.processingType === OrderProcessingType.Maker ? 0.0002 : 0.0005;
        return Math.abs(parameters.size) * parameters.price * rate;
    }

    getForOptions(parameters: FeeCalculationParameters): number {
        return Math.abs(parameters.size) * Math.min(
            parameters.underlyingPrice! * 0.00024,
            parameters.price * 0.10,
        );
    }
}

class DeribitFeeCalculationService implements IPlatformFeeCalculationService {
    getForSpots(): number {
        return 0;
    }

    getForFutures(parameters: FeeCalculationParameters): number {
        const rate = parameters.processingType === OrderProcessingType.Maker ? 0.00015 : 0.00035;
        return Math.abs(parameters.size) * parameters.price * rate;
    }

    getForOptions(parameters: FeeCalculationParameters): number {
        return Math.abs(parameters.size) * Math.min(
            parameters.underlyingPrice! * 0.0003,
            parameters.price * 0.125,
        );
    }
}

class PlatformFeeCalculationService {
    private readonly calculators = new Map<TargetPlatform, IPlatformFeeCalculationService>([
        [TargetPlatform.Binance, new BinanceFeeCalculationService()],
        [TargetPlatform.Deribit, new DeribitFeeCalculationService()],
    ]);

    supports(targetPlatform: TargetPlatform): boolean {
        return this.calculators.has(targetPlatform);
    }

    calculate(parameters: FeeCalculationParameters): number | null {
        const calculator = this.calculators.get(parameters.targetPlatform);
        
        if (parameters.processingType === OrderProcessingType.Manual ||
            calculator === undefined ||
            !Number.isFinite(parameters.size) ||
            !Number.isFinite(parameters.price) ||
            parameters.price < 0 ||
            (parameters.underlyingPrice !== null &&
                (!Number.isFinite(parameters.underlyingPrice) || parameters.underlyingPrice < 0))) {
            return null;
        }

        let charge: number | null;
        
        switch (parameters.assetType) {
            case AssetType.Spot:
                charge = calculator.getForSpots(parameters);
                break;
            case AssetType.Futures:
                charge = calculator.getForFutures(parameters);
                break;
            case AssetType.Call:
            case AssetType.Put:
                if (parameters.underlyingPrice === null) {
                    return null;
                }
                charge = calculator.getForOptions(parameters);
                break;
        }

        if (charge === null || !Number.isFinite(charge)) {
            return null;
        }

        return Math.round((charge + Number.EPSILON) * 100) / 100;
    }
}

export const platformFeeCalculationService = new PlatformFeeCalculationService();
